import type { m365AdminTr } from './m365-admin.tr.js';

/** English twin of m365-admin.tr.ts; the same keys. */
export const m365AdminEn: typeof m365AdminTr = {
  name: 'Microsoft 365',
  readOnly: 'read only',
  products: { both: 'Outlook mail and calendar', mail: 'Outlook mail', calendar: 'Outlook calendar', none: 'Outlook', onedrive: 'OneDrive', sharepoint: 'SharePoint' },
  status: {
    not_set_up: 'Not set up',
    incomplete: 'Setup not finished',
    off: 'Off for the company',
    failing: 'Test failed',
    working: 'Working',
  },
  unsupported: 'Microsoft 365 is for companies only; it cannot be set up in a personal space.',
  copy: 'Copy',
  copied: 'Copied',
  overview: {
    title: 'Built-in connections',
    hint: 'They come with Oppy. Once you set one up and turn it on, people connect with their own Microsoft accounts.',
    notSetUp: 'Set up once; then everyone connects their own account.',
    connected_one: '{{count}} person connected now',
    connected_other: '{{count}} people connected now',
    firmOn: 'On for the company',
    firmOff: 'Off for the company',
    setUp: 'Set up',
    manage: 'Manage',
    resume: 'Continue setup',
    catalog: 'Added connections',
  },
  testCodes: {
    tenant_not_found: { title: 'Tenant not found', action: 'Compare the tenant ID with the Overview page of the registration at Microsoft.' },
    app_not_found: { title: 'The app was not found in this tenant', action: 'Check the application ID and the tenant ID; the registration must be in this tenant.' },
    bad_secret: { title: 'The client secret is wrong', action: 'Make sure you copied the “Value” column, not the “Secret ID”.' },
    secret_expired: { title: 'The client secret has expired', action: 'Create a new client secret at Microsoft and enter it in step 2.' },
    no_secret: { title: 'No client secret entered', action: 'For direct sign-in, enter the client secret in step 2.' },
    public_client_disabled: { title: 'Sign-in with a code is off in the registration', action: 'In the registration, Authentication › Settings › “Allow public client flows” must be Enabled.' },
    redirect_not_https: { title: 'The redirect address is not https', action: 'Direct sign-in does not work with this address; choose “With a code” or give the server an https address.' },
    unreachable: { title: 'The server could not reach Microsoft', action: 'Allow outbound 443 to login.microsoftonline.com and graph.microsoft.com in the firewall, then try again.' },
    unexpected: { title: 'Microsoft gave an unexpected answer', action: 'Try again in a moment; if it persists, check the registration’s settings.' },
  },
  setup: {
    title: 'Microsoft 365 setup',
    intro: 'Done once, about 15 minutes. The client secret is stored encrypted on your server.',
    stepsLabel: 'Setup steps',
    steps: { s1: 'Register at Microsoft', s2: 'Enter the details', s3: 'Admin consent', s4: 'Test' },
    stepTitle: '{{n}} · {{title}}',
    stepDone: 'done',
    back: 'Back',
    next: 'Continue',
    register: {
      intro:
        'In the Microsoft Entra admin center (entra.microsoft.com), use an app registration for Oppy: your existing one if you have it (Entra ID › App registrations › All applications), otherwise create one with “New registration”. Add these to it:',
      redirect: { title: 'Redirect address', text: 'Authentication › Add Redirect URI › Web. Step 2 gives you the address with Copy.' },
      permissions: { title: 'Read permissions', text: 'API permissions › Add a permission › Microsoft Graph › Delegated permissions: User.Read, Mail.Read, Calendars.Read (openid, profile and offline_access should be listed too).' },
      consent: { title: 'Admin consent', text: 'On the same page, “Grant admin consent for …”: it consents to every permission listed. The button in step 3 asks only for the read permissions.' },
      secret: { title: 'Client secret', text: 'Certificates & secrets › New client secret. Copy the “Value” column; Microsoft shows it only once.' },
      noAppPermissions: 'Do not add application permissions: Oppy always works on behalf of the person and only reads.',
      guide: 'Step by step',
      guideSteps: {
        s1: 'At entra.microsoft.com, open Entra ID › App registrations › All applications. Choose the existing registration or create one with “New registration”: name “Oppy”, account type “Accounts in this organizational directory only”.',
        s2: 'On the registration’s Overview page, note the “Application (client) ID” and the “Directory (tenant) ID”; you enter them in step 2.',
        s3: 'Authentication › Add Redirect URI › Web: add the redirect address from step 2 (in the older layout, Add a platform › Web). Do not delete other addresses of the registration.',
        s4: 'API permissions › Add a permission › Microsoft Graph › Delegated permissions: User.Read, Mail.Read, Calendars.Read, openid, profile, offline_access.',
        s5: 'Certificates & secrets › New client secret: pick a duration, copy the “Value” and note the expiry date.',
        s6: 'Only if you will use sign-in “With a code”: Authentication › Settings › “Allow public client flows” = Enabled.',
      },
    },
    details: {
      intro: 'Copy them from the Overview page of your registration at Microsoft.',
      tenantId: 'Tenant ID',
      tenantIdHint: 'At Microsoft: Directory (tenant) ID',
      clientId: 'Application ID',
      clientIdHint: 'At Microsoft: Application (client) ID',
      guidPlaceholder: '00000000-0000-0000-0000-000000000000',
      guidError: 'It must look like Microsoft’s, 8-4-4-4-12 (e.g. 3f2a9c1e-5d7b-4c2e-9a10-…).',
      secret: 'Client secret',
      secretHint: 'At Microsoft: Certificates & secrets › New client secret',
      secretPlaceholder: 'Paste it; it is not shown again once saved',
      secretReplace: 'Paste a new one to replace it; leave empty to keep the saved secret',
      secretSaved: 'A client secret is saved',
      secretRequired: 'Direct sign-in needs a client secret.',
      secretOptional: 'Not needed for sign-in with a code.',
      expires: 'Client secret expiry (optional)',
      expiresHint: 'Microsoft shows it when the secret is created; the summary warns before it. Update it when you enter a new secret.',
      redirectTitle: 'Add this to the registration at Microsoft too',
      redirectPath: 'Authentication › Add Redirect URI › Web',
      redirectHttp:
        'This address is not https. For “Direct” sign-in Microsoft accepts only https (or localhost) redirect addresses; if your server has no https address, choose “With a code” below.',
      method: 'Advanced: sign-in method',
      methodHint: 'The default is “Direct”. If your server has no https address, “With a code” can be chosen.',
      methodLabel: 'Sign-in method',
      methods: {
        auth_code: { title: 'Direct', hint: 'People sign in on Microsoft’s page and come back to Oppy on their own. Needs an https address.' },
        device_code: { title: 'With a code', hint: 'People type the code Oppy shows on Microsoft’s page. “Allow public client flows” must be on in the registration.' },
      },
      recommended: 'Recommended',
      save: 'Save and continue',
      resetTitle: 'The tenant or the app changes',
      resetBody_one: '{{count}} person’s Microsoft connection is removed and its key deleted; they need to connect again.',
      resetBody_other: '{{count}} people’s Microsoft connections are removed and their keys deleted; each needs to connect again.',
      resetConfirm: 'Save and remove the connections',
    },
    consent: {
      intro: 'For people to connect with their own accounts, an administrator at Microsoft consents to these read permissions once.',
      who: 'A Global Administrator or a Cloud Application Administrator at Microsoft can do this; if you are not one, do this step together with them.',
      permissions: { user: 'The person’s name and e-mail address', mail: 'Reading mail', calendar: 'Reading the calendar' },
      button: 'Grant admin consent at Microsoft',
      noRedirectTitle: 'Grant the consent in the registration at Microsoft',
      noRedirect: 'This installation’s address is not https, so Microsoft cannot come back here. In the registration, use API permissions › “Grant admin consent for …”.',
      already: 'If you granted the consent in the registration at Microsoft, skip this step.',
    },
    test: {
      intro: 'Oppy tries the tenant, the application ID and the client secret at Microsoft; it reads no one’s data.',
      button: 'Test',
      ok: 'The connection works',
      answer: 'Microsoft answered in {{seconds}} s.',
      fix: 'Correct the details',
      openGuide: 'See step by step',
      finishTitle: 'Setup finished',
      finishOff: 'For people to use it, turn on “On for the company” on the summary page.',
      finishOn: 'People can connect their own accounts on the Connections page.',
      toSummary: 'Go to the summary',
    },
  },
  summary: {
    lead_one: 'People connect with their own Microsoft accounts. <b>{{count}} person</b> connected now.',
    lead_other: 'People connect with their own Microsoft accounts. <b>{{count}} people</b> connected now.',
    firm: 'On for the company',
    firmOffHint: 'While it is off no one can use it; connected accounts are kept.',
    incompleteTitle: 'Setup not finished',
    incompleteSecret: 'The client secret for direct sign-in is missing; no one can connect until it is entered.',
    incomplete: 'Setup details are missing; no one can connect until they are complete.',
    consentGranted: 'Admin consent was granted at Microsoft.',
    consentGrantedText: 'People no longer wait for consent when they connect. You can test the connection again if you like.',
    consentDenied: 'Admin consent was not granted.',
    consentDeniedText: 'If user consent is restricted in your tenant, people cannot connect without it; back in Oppy they see “Your Microsoft account could not be connected.” Grant consent in the registration at Microsoft, or try again in step 3 of the setup.',
    consentRetry: 'Open step 3',
    close: 'Close',
    features: {
      title: 'What is on',
      mail: { title: 'Reading mail', hint: 'Finding and reading mail; reading attachments when the person asks' },
      mailSend: { title: 'Sending mail', hint: 'Each send asks the employee’s approval; this approval cannot be turned off' },
      calendar: { title: 'Reading the calendar', hint: 'Looking at the calendar and meeting details, finding a free time (busy/free only)' },
      calendarWrite: { title: 'Creating meetings', hint: 'Each time asks the employee’s approval; invitations go after the approval' },
      onedrive: { title: 'Bringing documents from OneDrive', hint: 'The employee copies the documents they pick into Oppy once' },
      sharepoint: { title: 'Bringing documents from SharePoint', hint: 'The employee copies the documents they pick from the company’s sites into Oppy once' },
      needs: { mail_read: 'Turn on reading mail first', calendar_read: 'Turn on reading the calendar first' },
      entra: 'Before turning it on, add Files.Read.All (and Sites.Read.All for SharePoint) in Entra; guide §1.3.',
      note: 'Oppy deletes nothing in Microsoft 365; sending mail and creating meetings always ask the employee’s approval.',
      readOnly: 'Oppy sends, creates or deletes nothing in Microsoft 365.',
    },
    audience: {
      title: 'Who can use it',
      edit: 'Edit',
      everyone: 'Everyone',
      teams: 'Chosen teams: {{teams}}',
      noTeams: 'Chosen teams: no team chosen yet, no one can use it',
      moreTeams: '{{count}} more teams',
      hint: 'Team admins can turn it on and off for their own teams',
      turnedOff_one: 'turned off by the {{teams}} team',
      turnedOff_other: 'turned off by the {{teams}} teams',
      documents: { title: 'Brought documents', hint: 'At first only the person who brought them sees them. They can be put in a team with a warning; only super admins put them in Herkes.' },
    },
    connection: {
      title: 'Microsoft connection',
      open: 'Open settings',
      method: 'Sign-in',
      clientId: 'Application ID',
      expires: 'Client secret expires',
      expiresNone: 'not entered',
      expiresSoon_one: '{{date}} · {{count}} day left',
      expiresSoon_other: '{{date}} · {{count}} days left',
      expiresToday: '{{date}} · expires today',
      expired: '{{date}} · expired',
      noSecret: 'Client secret',
      noSecretValue: 'none (not needed with a code)',
      lastOk: 'Last test passed · {{when}}',
      lastFailed: 'Last test failed · {{when}} · {{reason}}',
      never: 'Not tested yet',
      retest: 'Test again',
      matchAccount: {
        title: 'The Microsoft account must match the person’s Oppy e-mail address',
        hint: 'While it is on, no one can connect a colleague’s mailbox (for example by sending them a sign-in code). Turn it off only if your people’s Oppy e-mail addresses differ from their Microsoft addresses.',
      },
    },
    remove: {
      hint: 'If you remove the setup, the Microsoft settings are deleted and everyone’s Microsoft connection is removed.',
      button: 'Remove setup',
      confirmTitle: 'Remove the Microsoft 365 setup?',
      confirmBody_one:
        'Everyone’s Microsoft connection is removed and its key deleted ({{count}} person connected now). The tenant, the application ID and the client secret are deleted too. This cannot be undone.',
      confirmBody_other:
        'Everyone’s Microsoft connection is removed and their keys deleted ({{count}} people connected now). The tenant, the application ID and the client secret are deleted too. This cannot be undone.',
    },
  },
  audienceDialog: {
    title: 'Who can use it?',
    everyoneHint: 'Everyone in the company. Team admins can turn it off for their own teams.',
    teams: 'Chosen teams',
    teamsHint: 'Only the people in the teams you choose.',
    teamsLabel: 'Teams',
    search: 'Search teams',
    members_one: '{{count}} person',
    members_other: '{{count}} people',
    turnedOff: 'turned off by the team admin',
    pickOne: 'Choose at least one team.',
    none: 'The company has no teams yet.',
    nothingFound: 'No team with this name.',
  },
  team: {
    note_one: 'As a team admin you can turn it on and off only for your <b>{{teams}}</b> team. Super admins manage the Microsoft settings.',
    note_other: 'As a team admin you can turn it on and off only for your <b>{{teams}}</b> teams. Super admins manage the Microsoft settings.',
    firmOn: 'On for the company',
    firmOff: 'Off for the company',
    notSetUp: 'Not set up',
    notSetUpText: 'Microsoft 365 is not set up in the company yet. Once super admins set it up and turn it on, you can turn it on and off for your team here.',
    for: 'For the {{team}} team',
    on: 'On',
    off: 'Off',
    offHint_one: 'If you turn it off, the {{count}} person in your team cannot use Microsoft 365; connected accounts are kept.',
    offHint_other: 'If you turn it off, the {{count}} people in your team cannot use Microsoft 365; connected accounts are kept.',
    onHint_one: 'If you turn it on, the {{count}} person in your team can use Microsoft 365.',
    onHint_other: 'If you turn it on, the {{count}} people in your team can use Microsoft 365.',
    blocked: {
      notSetUp: 'Microsoft 365 is not set up in the company yet.',
      firmOff: 'Off for the company; once super admins turn it on, you can turn it on and off for your team here.',
      notAllowed: 'Super admins opened Microsoft 365 to chosen teams only; this team is not one of them.',
    },
    connected: 'Connected in your team: {{connected}} / {{members}} people',
    none: 'No team you manage was found.',
  },
  audit: {
    filter: { label: 'Event', all: 'All', microsoft: 'Microsoft 365' },
    note: 'Mail and document content never appears here; only when, who and what.',
    fields: {
      entraTenantId: 'tenant ID',
      clientId: 'application ID',
      clientSecret: 'client secret',
      signInMethod: 'sign-in method',
      secretExpiresOn: 'client secret expiry',
    },
    changed: 'Changed: {{fields}}',
    method: { auth_code: 'Sign-in: Direct', device_code: 'Sign-in: With a code' },
    reset_one: '{{count}} person’s connection removed',
    reset_other: '{{count}} people’s connections removed',
    resetAll: 'Connected accounts removed',
    removed_one: '{{count}} person’s connection and key deleted',
    removed_other: '{{count}} people’s connections and keys deleted',
    removedNone: 'No one was connected',
    testOk: 'Passed',
    testFailed: 'Failed: {{reason}}',
    setting: {
      enabledOn: 'Turned on for the company',
      enabledOff: 'Turned off for the company',
      mailReadOn: 'Mail reading: turned on',
      mailReadOff: 'Mail reading: turned off',
      calendarReadOn: 'Calendar reading: turned on',
      calendarReadOff: 'Calendar reading: turned off',
      matchAccountOn: 'Microsoft account must match the Oppy e-mail: turned on',
      matchAccountOff: 'Microsoft account must match the Oppy e-mail: turned off',
      mailSendOn: 'Sending mail: turned on',
      mailSendOff: 'Sending mail: turned off',
      calendarWriteOn: 'Creating meetings: turned on',
      calendarWriteOff: 'Creating meetings: turned off',
      onedriveImportOn: 'Bringing documents from OneDrive: turned on',
      onedriveImportOff: 'Bringing documents from OneDrive: turned off',
      sharepointImportOn: 'Bringing documents from SharePoint: turned on',
      sharepointImportOff: 'Bringing documents from SharePoint: turned off',
      availability: 'Who can use it: {{from}} → {{to}}',
      teams_one: 'Chosen teams: {{count}} team',
      teams_other: 'Chosen teams: {{count}} teams',
    },
    audience: { everyone: 'Everyone', teams: 'Chosen teams' },
    teamOn: 'Turned on for the {{team}} team',
    teamOff: 'Turned off for the {{team}} team',
    anyTeamOn: 'Turned on for a team',
    anyTeamOff: 'Turned off for a team',
    scopes: {
      mail: 'mail',
      takvim: 'calendar',
      calendar: 'calendar',
      gonderme: 'sending',
      toplanti: 'meetings',
      dosyalar: 'files',
      siteler: 'sites',
      mail_send: 'sending',
      calendar_write: 'meetings',
      files: 'files',
      sites: 'sites',
    },
    upgrade: 'connected again for a new permission',
    deviceCode: 'signed in with a code',
    disconnected: {
      user: 'Removed by themselves · key deleted',
      access_closed: 'Key deleted because their access was turned off',
      data_deleted: 'Key deleted because they deleted their data',
      setup_changed: 'Key deleted because the setup changed',
      other: 'Key deleted',
    },
    signIn: {
      state_invalid: 'The sign-in could not be verified; it had to start again',
      access_denied: 'The person did not give permission at Microsoft',
      consent_required: 'Admin consent was needed',
      personal_account: 'A personal Microsoft account was refused',
      other_tenant: 'Another company’s account was refused',
      guest_account: 'A guest account was refused',
      wrong_account: 'The Microsoft account did not match the person’s Oppy e-mail address',
      id_token_invalid: 'Microsoft’s answer could not be verified',
      exchange_failed: 'Microsoft did not complete the sign-in',
      not_available: 'It was off for the person',
      unavailable: 'Microsoft could not be reached',
      expired: 'The code expired',
      declined: 'The person declined at Microsoft',
      unknown: 'The sign-in could not be completed',
    },
    refresh: {
      microsoft_reconnect: 'The Microsoft sign-in is no longer valid · asked to connect again',
      invalid_grant: 'The Microsoft sign-in is no longer valid · asked to connect again',
      needs_reconnect: 'The Microsoft sign-in is no longer valid · asked to connect again',
      password_changed: 'Password changed · asked to connect again',
      microsoft_consent_required: 'Admin consent is needed',
      consent_required: 'Admin consent is needed',
      unknown: 'Asked to connect again',
    },
    consentRequested: 'Admin consent was needed while connecting; the admins were told',
    notified_one: '{{count}} admin notified',
    notified_other: '{{count}} admins notified',
    tools: {
      mail_ara: 'Search mail',
      mail_oku: 'Read mail',
      eki_oku: 'Read attachment',
      takvime_bak: 'Look at the calendar',
      toplanti_ayrintisi: 'Meeting details',
      uygun_saat_bul: 'Find a free time',
    },
    items_one: '{{count}} result',
    items_other: '{{count}} results',
    write: {
      product: 'Microsoft 365',
      recipients_one: '{{count}} recipient',
      recipients_other: '{{count}} recipients',
      external_one: '{{count}} outside the company',
      external_other: '{{count}} outside the company',
      attendees_one: '{{count}} attendee',
      attendees_other: '{{count}} attendees',
      noAttendees: 'no invitation, calendar only',
      teams: 'Teams meeting',
      noTeams: 'no Teams link',
      edited: 'edited on the card',
      noContent: 'content is not kept',
      draftLeft: 'the draft stayed in Drafts',
      notSent: {
        cancelled: "The user cancelled · 'Cancel' on the approval card",
        expired: 'The card expired',
        stopped: 'Stopped',
        failed: 'Could not be sent',
        unknown: 'Unknown whether it was sent',
      },
      notCreated: {
        cancelled: "The user cancelled · 'Cancel' on the approval card",
        expired: 'The card expired',
        stopped: 'Stopped',
        failed: 'Could not be created',
        unknown: 'Unknown whether it was created',
      },
      codes: {
        draft_changed: 'the draft had changed in Outlook',
        verify_mismatch: "Outlook's draft differed from the card",
        microsoft_busy: 'Microsoft was too busy',
        microsoft_forbidden: 'Microsoft did not allow it',
        microsoft_not_found: 'Microsoft did not find the mail',
        microsoft_rejected: 'Microsoft refused the request',
        microsoft_failed: 'Microsoft had a problem',
        microsoft_scopes_required: 'a new permission was needed',
        recipient_not_allowed: 'the recipient was not on the trial list',
        feature_off: 'the company had turned it off',
        timeout: "Microsoft's answer never came",
      },
    },
    documents: {
      source: { onedrive: 'OneDrive', sharepoint: 'SharePoint', outlook: 'Outlook attachment', mixed: 'Several sources' },
      files_one: '{{count}} file',
      files_other: '{{count}} files',
      to: '{{files}} → {{place}}',
      place: {
        user: 'Personal',
        team: 'Team: {{name}}',
        workspace: 'Workspace: {{name}}',
        teamAny: 'Team',
        workspaceAny: 'Workspace',
        organization: 'Everyone',
        conversation: 'This conversation',
      },
      acknowledged: '(warning confirmed)',
      refused_one: '{{count}} file could not be fetched',
      refused_other: '{{count}} files could not be fetched',
      duplicate_one: '{{count}} file was already there',
      duplicate_other: '{{count}} files were already there',
      refetch: { ok: 'updated', unchanged: 'no change at the source', failed: 'could not be fetched again' },
      codes: {
        source_forbidden: 'no permission to see the file in Microsoft',
        source_not_found: 'the file was not found in Microsoft; it may have been deleted or moved',
        source_too_large: 'the file was over the size limit',
        source_unsupported: 'the file type could not be read',
        source_malware: 'Microsoft found malware in the file',
        source_empty: 'the file was empty',
        source_off: 'fetching documents from Microsoft 365 was turned off',
        source_not_connected: 'no Microsoft account was connected',
        microsoft_scopes_required: 'a new permission was needed',
        source_unavailable: 'Microsoft could not be reached',
      },
    },
  },
};
